import {api, RequestParam} from "@/shared/api";

import {RecordingStatusPanel} from "@/shared/recording/recordingStatusPanel";
import {SwitchTaskDialog} from "@/features/projecttime/switchTaskDialog";
import {ProjectTimeInfoPanel} from "@/shared/projecttime/projectTimeInfoPanel";
import {TaskDropdownWithRecommendations} from "@/shared/task/taskDropdownWithRecommendations";
import {DescriptionTypeahead} from "@/shared/ui/descriptionTypeahead";
import {ProjectTimeTable} from "./projectTimeTable";
import {statusElementsHolder} from "@/shared/recording/statusElementsHolder";
import {isProjectTimeDateTimeChangeAllowed} from "@/shared/securityHelper";
import {FormUtil} from "@/shared/forms/formUtil";
import * as BootstrapModal from "@/shared/ui/bootstrapModal";
import {Analytics} from "@/shared/analytics";
import {SecurityStatus} from "@/shared/securityStatus";
import {
	getCurrentISOOffset,
	getISOZonedDateTime,
	parseDateTimeZone,
	parseISOZonedDateTime,
	Temporal
} from "@/shared/utils/datefunctions";
import {ErrorResponse, ProjectTime, ProjectTimeStatus, RunningProjectTime, Task} from "@/shared/timr";

export class ProjectTimeStatusPanel extends RecordingStatusPanel<ProjectTimeStatus, RunningProjectTime, ProjectTime> {

	declare private projectTimeInfoPanel: ProjectTimeInfoPanel;
	declare private taskDropdown: TaskDropdownWithRecommendations;
	declare private descriptionTypeahead: DescriptionTypeahead;
	declare private switchTaskDialog: SwitchTaskDialog;

	protected init() {
		super.init();

		this.taskDropdown = new TaskDropdownWithRecommendations({
			rootElement: $("#projectTimeStatusTaskDropdown"),
			allowSelectionOfNotBookable: false,
			hideInactive: true,
			emptyMessage: messages["task.select.task"],
			clearable: false,
			getReferenceDate: () => {
				const startDate = $("#projectTimeStatusStartDate").val() as string;
				const startTime = $("#projectTimeStatusStartTime").val() as string || "0";
				const startTimeZone = $("#projectTimeStatusStartTimeZone").val() as string;
				return parseDateTimeZone(startDate, startTime, startTimeZone) ?? Temporal.Now.zonedDateTimeISO();
			}
		}, {
			loadRootNodesURL: "recording/loadAllTasks.json",
			loadSubNodesURL: "recording/treeLoad.json",
			filterNodesURL: "recording/filterTasks.json"
		});
		$(this.taskDropdown).on(TaskDropdownWithRecommendations.EVENT_SELECTED, (_, task: Task) => {
			this.selectTask(task);
		});
		$("#projectTimeStatusStartDate").on("change", () => this.taskDropdown.updateProjectTimeCount());
		$(document).on(ProjectTimeTable.EVENT_DELETE_RECORDING, () => this.taskDropdown.updateProjectTimeCount());

		$(this).on(RecordingStatusPanel.EVENT_QUICK_SWITCHED, (_, data: ProjectTimeStatus) => {
			this.clearChanged();
			this.updateStatus(data);
			this.taskDropdown.updateProjectTimeCount();
			this.showStatusUpdateMessage(messages["recording.switched"]);
			Analytics.trackEvent("Project Time - Quick Switch", "Project Time");
			this.endSwitchRecording();
		});

		$(this).on(RecordingStatusPanel.EVENT_QUICK_STARTED, (_, data: ProjectTimeStatus) => {
			this.clearChanged();
			this.updateStatus(data);
			this.showStatusUpdateMessage(messages["recording.started"]);
			this.endQuickStartRecording();
		});

		this.config.panel.find(".taskQuery").focus(e => {
			this.currentFocusedField = $(e.target).attr("name");
		}).blur(() => {
			this.currentFocusedField = null;
		});

		this.config.panel.find(".billable").change(e => {
			this.setBillable($(e.target).is(":checked"));
			this.updatePanelBorderColor();
			this.queueUpdate();
		});

		this.descriptionTypeahead = new DescriptionTypeahead({
			element: this.config.panel.find((".descriptionTypeahead")),
			url: "filterProjectTimeDescriptions.json"
		});
		$(this.descriptionTypeahead).on(DescriptionTypeahead.EVENT_CHANGED, () => {
			this.config.panel.find(".description").change();
			this.queueUpdate();
		});

		this.projectTimeInfoPanel = new ProjectTimeInfoPanel($("#projectTimeInfoPanel"));

		this.switchTaskDialog = new SwitchTaskDialog({
			container: $("#projectTimeSwitchTaskDialog")
		});
		$(this.switchTaskDialog)
			.on(SwitchTaskDialog.EVENT_CANCELED, () => {
				this.activateUpdater();
				this.endSwitchRecording();
			});

		this.switchTaskDialog.connect(this.taskDropdown);
	}

	public get projectTimeStatus(): ProjectTimeStatus {
		return this.currentStatus;
	}

	public shouldSwitchOrStartRecording(task: Task) {
		if (this.pluckRunningRecording(this.currentStatus)) {
			this.shouldSwitchRecording(task.id);
		} else {
			this.selectTask(task);
			this.shouldStartRecording();
		}
	}

	protected pluckRunningRecording(recordingStatus: ProjectTimeStatus | null): RunningProjectTime | null {
		return recordingStatus ? recordingStatus.runningProjectTime : null;
	}

	protected pluckLastRecording(recordingStatus: ProjectTimeStatus | null): ProjectTime | null {
		return recordingStatus ? recordingStatus.lastProjectTime : null;
	}

	protected pluckRecordingCategory(): string {
		return "Project Time";
	}

	protected isDateTimeChangeAllowed(): boolean {
		return isProjectTimeDateTimeChangeAllowed();
	}

	protected sendUpdate(): JQuery.jqXHR<ProjectTimeStatus> {
		const startDate = $("#projectTimeStatusStartDate").val() as string;
		const startTime = $("#projectTimeStatusStartTime").val() as string || "0";
		const startTimeZone = $("#projectTimeStatusStartTimeZone").val() as string;
		const startDateTime = parseDateTimeZone(startDate, startTime, startTimeZone);
		$("#projectTimeStatusStartDateTime").val(getISOZonedDateTime(startDateTime));

		const endDate = $("#projectTimeStatusEndDate").val() as string;
		const endTime = $("#projectTimeStatusEndTime").val() as string;
		const endTimeZone = $("#projectTimeStatusEndTimeZone").val() as string;
		const endDateTime = parseDateTimeZone(endDate, endTime, endTimeZone);
		$("#projectTimeStatusEndDateTime").val(getISOZonedDateTime(endDateTime));

		$("#projectTimeStatusBreakTime").val(($("#projectTimeStatusBreakTime").val() as string).replace(/\D/g, ""));
		const params = $("#projectTimeStatusForm").serialize() + "&" + $.param({
			localTimeZone: getCurrentISOOffset()
		});
		return api("recording/updateProjectTimeRecording.json")
			.post(params)
			.done((data) => {
				this.changedFields = $.grep(this.changedFields, (value) => {
					return value !== "startDate" && value !== "startTime" && value !== "startTimeZone";
				});
				$("#projectTimeStatusVersion").val(data.runningProjectTime.version);
				this.updateStatus(data);
			});
	}

	protected shouldUpdateEndDateAndTime(): boolean {
		return !this.switchTaskDialog.isVisible();
	}

	protected validateSecurityStatus(): boolean {
		if (!SecurityStatus.isAddProjectTimeAllowed()) {
			BootstrapModal.info({
				title: "account.expired.projecttime.title",
				message: "account.expired.projecttime",
				escapeText: false
			});
			return false;
		}
		return true;
	}

	protected startRecording(): JQuery.jqXHR<ProjectTimeStatus> {
		Analytics.trackEvent("Project Time - Start", "Project Time");
		this.updateStartTime(this.currentStatus);
		const form = $("#projectTimeStatusForm");
		const params: RequestParam[] = form.serializeArray();

		const now = Temporal.Now.zonedDateTimeISO();
		params.push({name: "timestamp", value: now.epochMilliseconds});
		params.push({name: "localTimeZone", value: now.offset})

		return api("recording/startProjectTimeRecording.json")
			.post(params)
			.done((data) => {
				FormUtil.removeAllFeedback(form);
				this.clearChanged();
				this.updateStatus(data);
				this.showStatusUpdateMessage(messages["recording.started"]);
			}).fail((response: ErrorResponse) => {
				if (response.responseJSON?.fieldErrors) {
					FormUtil.markFieldErrors(form, response.responseJSON.fieldErrors);
				}
			});
	}

	protected startPauseRecording(): JQuery.jqXHR<ProjectTimeStatus> {
		Analytics.trackEvent("Project Time - Pause", "Project Time");
		const now = Temporal.Now.zonedDateTimeISO();
		return api("recording/pauseProjectTimeRecording.json")
			.post({
				pausedAt: getISOZonedDateTime(now),
				localTimeZone: now.offset,
				recordId: this.config.panel.find(".recordId").val(),
				timestamp: now.epochMilliseconds
			})
			.done((data) => {
				this.updateStatus(data);
				this.descriptionTypeahead.setNeedsReset();
				this.showStatusUpdateMessage(messages["recording.paused"]);
			})
			.fail((response: ErrorResponse) => {
				if (response.responseJSON?.code === "E0200") {
					this.fetchRecordingStatus(true);
				}
			})
			.always(() => this.endPauseRecording());
	}

	protected startResumeRecording(): JQuery.jqXHR<ProjectTimeStatus> {
		Analytics.trackEvent("Project Time - Resume", "Project Time");
		const now = Temporal.Now.zonedDateTimeISO();
		return api("recording/resumeProjectTimeRecording.json")
			.post({
				resumedAt: getISOZonedDateTime(now),
				localTimeZone: now.offset,
				recordId: this.config.panel.find(".recordId").val(),
				timestamp: now.epochMilliseconds
			})
			.done((data) => {
				this.updateStatus(data);
				this.descriptionTypeahead.setNeedsReset();
				this.showStatusUpdateMessage(messages["recording.resumed"]);
			})
			.fail((response: ErrorResponse) => {
				if (response.responseJSON?.code === "E0200") {
					this.fetchRecordingStatus(true);
				}
			})
			.always(() => this.endResumeRecording());
	}

	protected stopRecording(): JQuery.jqXHR<ProjectTimeStatus> {
		Analytics.trackEvent("Project Time - Stop", "Project Time");
		this.updateEndTime(this.currentStatus);
		const form = $("#projectTimeStatusForm");
		const now = Temporal.Now.zonedDateTimeISO();
		const params = form.serialize() + "&" + $.param({
			localTimeZone: now.offset,
			timestamp: now.epochMilliseconds
		});
		return api("recording/stopProjectTimeRecording.json")
			.post(params)
			.done((data) => {
				FormUtil.removeAllFeedback(form);
				this.clearChanged();
				this.updateStatus(data);
				this.taskDropdown.updateProjectTimeCount();
				this.descriptionTypeahead.setNeedsReset();
				this.showStatusUpdateMessage(messages["recording.stopped"]);
			}).fail((response: ErrorResponse) => {
				if (response.responseJSON?.fieldErrors) {
					FormUtil.markFieldErrors(form, response.responseJSON.fieldErrors);
				}
			});
	}

	protected deleteRecording(): JQuery.jqXHR<ProjectTimeStatus> {
		Analytics.trackEvent("Project Time - Delete", "Project Time");
		const form = $("#projectTimeStatusForm");
		return api("recording/deleteProjectTimeRecording.json")
			.post({
				localTimeZone: getCurrentISOOffset(),
				recordId: this.config.panel.find(".recordId").val()
			})
			.done((data) => {
				FormUtil.removeAllFeedback(form);
				this.clearChanged();
				this.updateStatus(data);
				this.showStatusUpdateMessage(messages["recording.deleted"]);
				this.descriptionTypeahead.setNeedsReset();
			})
			.fail((response: ErrorResponse) => {
				if (response.responseJSON?.code === "E0200") {
					this.fetchRecordingStatus(true);
				}
			});
	}

	protected shouldSwitchRecording(taskId: number = null) {
		this.prepareStopRecording(() => {
			this.setLoading(this.config.panel.find(".switchButton"));
			this.immediatelyUpdateIfNecessary(() => {
				this.startSwitchRecording(taskId);
			});
		});
	}

	protected shouldStartRecording() {
		if (this.validateStart()) {
			const startButton = this.config.panel.find(".startButton");
			this.setLoading(startButton);
			this.startRecording().always(() => {
				this.resetLoading(startButton);
			});
		}
	}

	protected startSwitchRecording(taskId: number = null): JQuery.jqXHR {
		Analytics.trackEvent("Project Time - Switch", "Project Time");
		this.updateEndTime(this.currentStatus);
		const form = $("#projectTimeStatusForm");
		const params: RequestParam[] = form.serializeArray();
		return api("recording/validateProjectTimeRecording.json")
			.get(params)
			.done(() => {
				const changed = !this.notChanged("endDate") || !this.notChanged("endTime") || !this.notChanged("endTimeZone");
				params.push({name: "changedNewStart", value: changed});
				params.push({name: "localTimeZone", value: getCurrentISOOffset()});

				if (taskId == null) {
					const endDateTimeString = params.find(e => e.name === "end").value as string;
					const endTime = parseISOZonedDateTime(endDateTimeString);

					this.switchTaskDialog.show(endTime, (taskId) => this.performTaskSwitch(params, taskId));
				} else {
					void this.performTaskSwitch(params, taskId);
				}
			}).fail((response: ErrorResponse) => {
				if (response.responseJSON?.fieldErrors) {
					FormUtil.markFieldErrors(form, response.responseJSON.fieldErrors);
				}
				this.endSwitchRecording();
			});
	}

	private performTaskSwitch(params: RequestParam[], taskId: number): JQuery.jqXHR<ProjectTimeStatus> {
		// Enrich a copy, otherwise a retry after a failed switch sends the task and timestamp of the previous attempt as well
		const switchParams: RequestParam[] = [
			...params,
			{name: "newTaskId", value: taskId},
			{name: "timestamp", value: Date.now()}
		];

		return api("recording/switchProjectTimeRecording.json")
			.post(switchParams)
			.done((data) => {
				FormUtil.removeAllFeedback($("#projectTimeStatusForm"));
				this.clearChanged();
				this.updateStatus(data);
				this.taskDropdown.updateProjectTimeCount();
				this.descriptionTypeahead.setNeedsReset();
				this.showStatusUpdateMessage(messages["recording.switched"]);
				Analytics.trackEvent("Project Time - Switch", "Project Time");
			})
			.always(() => {
				this.activateUpdater();
				this.endSwitchRecording();
			});
	}

	protected startQuickSwitchRecording(recordingId: number): JQuery.jqXHR<ProjectTimeStatus> {
		this.updateEndTime(this.currentStatus);
		const form = $("#projectTimeStatusForm");
		const params: RequestParam[] = form.serializeArray();
		const changed = !this.notChanged("endDate") || !this.notChanged("endTime") || !this.notChanged("endTimeZone");
		const now = Temporal.Now.zonedDateTimeISO();
		params.push({name: "changedNewStart", value: changed});
		params.push({name: "sourceProjectTimeId", value: recordingId});
		params.push({name: "localTimeZone", value: now.offset});
		params.push({name: "timestamp", value: now.epochMilliseconds});
		if (this.pluckRunningRecording(this.currentStatus)) {
			Analytics.trackEvent("Project Time - Quick Switch", "Project Time");
			return api("recording/quickSwitchProjectTimeRecording.json")
				.post(params)
				.done((data) => {
					FormUtil.removeAllFeedback(form);
					$(this).triggerHandler(RecordingStatusPanel.EVENT_QUICK_SWITCHED, [data]);
					this.descriptionTypeahead.setNeedsReset();
				})
				.fail((response: ErrorResponse) => {
					if (response.responseJSON?.fieldErrors) {
						FormUtil.markFieldErrors(form, response.responseJSON.fieldErrors);
					}
					this.endSwitchRecording();
				});
		} else {
			Analytics.trackEvent("Project Time - Quick Start", "Project Time");
			return api("recording/quickSwitchProjectTimeRecording.json")
				.post(params)
				.done((data) => {
					$(this).triggerHandler(RecordingStatusPanel.EVENT_QUICK_STARTED, [data]);
					this.descriptionTypeahead.setNeedsReset();
				})
				.fail(() => {
					this.resetLoading(this.config.panel.find(".startButton"));
				});
		}
	}

	protected updateRecordingInfoPanel(recording: RunningProjectTime | null, running: boolean, forceUpdate = true) {
		const runningRecording = running ? recording : null;
		if (recording && recording.task) {
			this.projectTimeInfoPanel.update(recording.task.id, recording.task.description, runningRecording, null, forceUpdate);
		}
	}

	protected clearFields() {
		super.clearFields();

		this.setBillable(false);
	}

	protected updateFieldsWithRunningRecording(runningRecording: RunningProjectTime) {
		super.updateFieldsWithRunningRecording(runningRecording);

		this.setBillable(runningRecording.billable);
		this.onTaskChanged();
	}

	protected updateFields(recordingStatus: ProjectTimeStatus) {
		super.updateFields(recordingStatus);

		const selectedTaskId = this.taskDropdown.getSelectedId();
		if (recordingStatus.runningProjectTime) {
			if (this.notChanged("task") && this.notFocused("taskQuery")) {
				this.taskDropdown.setSelectedTask(recordingStatus.runningProjectTime.task);
			}
		} else {
			if (this.notChanged("task") && this.notFocused("taskQuery")) {
				if (recordingStatus.lastProjectTime?.task) {
					this.taskDropdown.setSelectedTask(recordingStatus.lastProjectTime.task);
				} else {
					this.taskDropdown.setSelectedTask(null);
				}
			}
			if (recordingStatus.lastProjectTime?.task && this.notChanged("billable")) {
				this.setBillable(recordingStatus.lastProjectTime.task.billable);
			}
			const selectedTaskData = this.taskDropdown.getSelectedData();
			if (selectedTaskData) {
				this.projectTimeInfoPanel.update(selectedTaskData.id, selectedTaskData.description, this.pluckRunningRecording(this.currentStatus));
			}
		}
		if (selectedTaskId !== this.taskDropdown.getSelectedId()) {
			this.onTaskChanged();
		}
		this.updatePanelBorderColor();
	}

	protected updatePanelBorderColor() {
		const panelBody = this.config.panel.find(".panel-body");
		const icon = this.config.panel.find(".recording-status-indicator");

		if (this.pluckRunningRecording(this.currentStatus)) {
			const billable = this.config.panel.find(".billable").is(":checked");

			icon.toggleClass("icon-BILLABLE", billable);
			icon.toggleClass("icon-NOT_BILLABLE", !billable);

			panelBody.toggleClass("border-BILLABLE", billable);
			panelBody.toggleClass("border-NOT_BILLABLE", !billable);
		} else {
			panelBody.removeClass("border-BILLABLE").removeClass("border-NOT_BILLABLE");
			icon.removeClass("icon-BILLABLE").removeClass("icon-NOT_BILLABLE");
		}
	}

	protected onStatusUpdated(recordingStatus: ProjectTimeStatus) {
		super.onStatusUpdated(recordingStatus);
		statusElementsHolder.updateRunningProjectTime(this.pluckRunningRecording(recordingStatus));
	}

	private selectTask(task: Task) {
		this.config.panel.find("input[name='task.id']").val(task.id);
		this.setBillable(task.billable);

		if ($.inArray("billable", this.changedFields) === -1) {
			this.changedFields[this.changedFields.length] = "billable";
		}
		if ($.inArray("task", this.changedFields) === -1) {
			this.changedFields[this.changedFields.length] = "task";
		}
		this.projectTimeInfoPanel.update(task.id, task.description, this.pluckRunningRecording(this.currentStatus));
		this.updatePanelBorderColor();
		this.onTaskChanged();
		this.queueUpdate();
	}

	private setBillable(billable: boolean) {
		this.config.panel.find(".billable").prop("checked", billable);
		this.config.panel.find(`input[name="billable"]`).val(String(billable));
	}

	private onTaskChanged() {
		this.descriptionTypeahead.setFilter({taskId: this.taskDropdown.getSelectedId()});
	}
}
